import { getNostoAccountId } from "@/config";

const NOSTO_SEARCH_ENDPOINT = "https://search.nosto.com/v1/graphql";

// Full results query: request the product id (we hydrate the rest of the card
// data from Shopify so the UI stays identical), the total for paging, and the
// facets the account has configured so we can render filters.
const SEARCH_RESULTS_QUERY = `
  query ($accountId: String!, $query: String, $products: InputSearchProducts, $sessionParams: InputSearchQuery) {
    search(accountId: $accountId, query: $query, products: $products, sessionParams: $sessionParams) {
      query
      redirect
      products {
        hits {
          productId
        }
        total
        size
        from
        fuzzy
        facets {
          __typename
          ... on SearchTermsFacet {
            id
            field
            type
            name
            data {
              value
              count
              selected
            }
          }
          ... on SearchStatsFacet {
            id
            field
            type
            name
            min
            max
          }
        }
      }
    }
  }
`;

// Predictive/typeahead query: the overlay only renders image, title and price,
// so we can read those fields straight from Nosto without hydration.
const SEARCH_PREDICTIVE_QUERY = `
  query ($accountId: String!, $query: String, $products: InputSearchProducts, $keywords: InputSearchKeywords, $sessionParams: InputSearchQuery) {
    search(accountId: $accountId, query: $query, products: $products, keywords: $keywords, sessionParams: $sessionParams) {
      query
      redirect
      products {
        hits {
          productId
          name
          url
          imageUrl
          price
          listPrice
          priceCurrencyCode
        }
        total
        size
        from
        fuzzy
      }
      keywords {
        hits {
          keyword
          _redirect
          _highlight {
            keyword
          }
        }
        total
      }
    }
  }
`;

// Best-effort retrieval of Nosto search session params (for personalization).
// Resolves with an empty object if Nosto is unavailable or times out.
const getSessionParams = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.nostojs !== "function") {
      resolve({});
      return;
    }

    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || {});
    };

    // Don't let a slow/absent Nosto block the search request.
    const timeout = setTimeout(() => done({}), 1000);

    try {
      window.nostojs((api) => {
        if (typeof api.getSearchSessionParams !== "function") {
          clearTimeout(timeout);
          done({});
          return;
        }
        api
          .getSearchSessionParams()
          .then((params) => {
            clearTimeout(timeout);
            done(params);
          })
          .catch(() => {
            clearTimeout(timeout);
            done({});
          });
      });
    } catch (error) {
      clearTimeout(timeout);
      done({});
    }
  });

// Run a callback against the nostojs API when it's available. Tracking is
// strictly best-effort: if the Nosto script is blocked or errors, search must
// keep working, so failures are swallowed (mirrors getSessionParams above).
const withNostoApi = (callback) => {
  if (typeof window === "undefined" || typeof window.nostojs !== "function") {
    return;
  }

  try {
    window.nostojs((api) => {
      try {
        callback(api);
      } catch (error) {
        console.error("Nosto analytics error:", error);
      }
    });
  } catch (error) {
    console.error("Nosto analytics error:", error);
  }
};

/**
 * Marks a query as an organic search. Must be called on search input submit,
 * before the search request is sent / the browser navigates to the SERP.
 */
export const recordNostoSearchSubmit = (query) => {
  withNostoApi((api) => {
    if (typeof api.recordSearchSubmit === "function") {
      api.recordSearchSubmit(query);
    }
  });
};

/**
 * Nosto identifies products by the numeric Shopify id, while hydrated Shopify
 * products carry a gid (gid://shopify/Product/123).
 */
export const toNostoProductId = (id) => {
  if (!id) {
    return null;
  }
  return String(id).replace(/^gid:\/\/shopify\/Product\//, "");
};

/**
 * Tracks a product click from search results.
 * `type` is "serp" or "autocomplete"; `hit` is { productId, url }.
 */
export const recordNostoSearchClick = (type, hit) => {
  if (!hit?.productId) {
    return;
  }

  withNostoApi((api) => {
    if (typeof api.recordSearchClick === "function") {
      api.recordSearchClick(type, hit);
    }
  });
};

// The typeahead navigates to the SERP on keyword click, so the "this search
// came from a keyword" signal has to survive the page load. It's stashed in
// sessionStorage and consumed by the SERP's first tracked search.
const NOSTO_KEYWORD_FLAG_KEY = "nostoSearchKeywordTerm";

export const setNostoKeywordFlag = (term) => {
  try {
    window.sessionStorage.setItem(NOSTO_KEYWORD_FLAG_KEY, term);
  } catch (error) {
    // Storage unavailable (e.g. blocked); keyword attribution is lost but
    // search must proceed.
  }
};

/**
 * Reads and clears the keyword flag. Returns true when the given term was the
 * one submitted via an autocomplete keyword click.
 */
export const consumeNostoKeywordFlag = (term) => {
  try {
    const stored = window.sessionStorage.getItem(NOSTO_KEYWORD_FLAG_KEY);
    if (stored === null) {
      return false;
    }
    window.sessionStorage.removeItem(NOSTO_KEYWORD_FLAG_KEY);
    return stored === term;
  } catch (error) {
    return false;
  }
};

// Tracks a completed search request to Nosto analytics. The full request and
// full result objects are required for valid tracking payloads.
const recordNostoSearch = ({ type, request, result, isKeyword = false }) => {
  withNostoApi((api) => {
    if (typeof api.recordSearch === "function") {
      api.recordSearch(type, request, result, { isKeyword });
    }
  });
};

const postSearch = async ({ query, variables }) => {
  const response = await fetch(NOSTO_SEARCH_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });

  const result = await response.json();

  // Log errors internally; never surface raw Nosto errors to the end user.
  if (result.errors) {
    console.error("Nosto search errors:", result.errors);
  }

  return result.data?.search || null;
};

/**
 * Full text search for the /search results page.
 * Returns ranked product ids (to hydrate via Shopify), the total count and the
 * configured facets.
 *
 * `filters` is the Nosto `products.filter` array, e.g.
 *   [{ field: "ai.primaryColor", value: ["Black"] }]              // terms facet
 *   [{ field: "listPrice", range: [{ gte: "100", lte: "300" }] }] // stats facet
 *
 * `track` ("serp") sends the search to Nosto analytics; `isKeyword` should be
 * true when the search was triggered by an autocomplete keyword click.
 */
export const nostoProductSearch = async ({
  accountId,
  currencyCode,
  query,
  size = 12,
  from = 0,
  filters = [],
  track = null,
  isKeyword = false,
}) => {
  const resolvedAccountId = accountId || getNostoAccountId(currencyCode);

  if (!resolvedAccountId) {
    console.error("Nosto search: no account id available");
    return { productIds: [], total: 0, from, size, facets: [], redirect: null };
  }

  try {
    const sessionParams = await getSessionParams();

    const products = { size, from };
    if (Array.isArray(filters) && filters.length) {
      products.filter = filters;
    }

    const search = await postSearch({
      query: SEARCH_RESULTS_QUERY,
      variables: {
        accountId: resolvedAccountId,
        query,
        products,
        sessionParams,
      },
    });

    // Redirect responses navigate away immediately, so there is no result
    // impression to track.
    if (track && search && !search.redirect) {
      recordNostoSearch({
        type: track,
        request: { query, products },
        result: search,
        isKeyword,
      });
    }

    const result = search?.products;
    const hits = result?.hits || [];

    return {
      productIds: hits.map((hit) => hit.productId).filter(Boolean),
      total: result?.total || 0,
      from: result?.from ?? from,
      size: result?.size ?? size,
      facets: result?.facets || [],
      redirect: search?.redirect || null,
    };
  } catch (error) {
    console.error("Nosto search request failed:", error);
    return { productIds: [], total: 0, from, size, facets: [], redirect: null };
  }
};

/**
 * Category Merchandising 2.0: product listing for a Shopify collection.
 *
 * Nosto returns the collection's products in merchandised order (pins,
 * sequences, scheduled rules, personalisation). Like `nostoProductSearch`,
 * only ids come back and the cards are hydrated from Shopify so the grid
 * stays identical to the Shopify-driven version.
 *
 * `categoryId` is the numeric Shopify collection id (fully supported for
 * Shopify accounts); `categoryPath` is the collection *title*, which Nosto
 * recommends sending alongside the id for analytics.
 *
 * `sort` is the Nosto `products.sort` array, e.g. [{ field: "price", order: "asc" }].
 * Leave it empty for merchandised order: an explicit sort overrides
 * merchandising rules.
 *
 * `track` ("category") sends the impression to Nosto analytics.
 */
export const nostoCategoryProducts = async ({
  accountId,
  currencyCode,
  categoryId,
  categoryPath,
  size = 12,
  from = 0,
  filters = [],
  sort = [],
  track = null,
}) => {
  const resolvedAccountId = accountId || getNostoAccountId(currencyCode);
  const empty = { productIds: [], total: 0, from, size, facets: [], ok: false };

  if (!resolvedAccountId) {
    console.error("Nosto category: no account id available");
    return empty;
  }

  if (!categoryId && !categoryPath) {
    console.error("Nosto category: no categoryId or categoryPath given");
    return empty;
  }

  try {
    const sessionParams = await getSessionParams();

    const products = { size, from };
    if (categoryId) {
      products.categoryId = String(categoryId);
    }
    if (categoryPath) {
      products.categoryPath = categoryPath;
    }
    if (Array.isArray(filters) && filters.length) {
      products.filter = filters;
    }
    if (Array.isArray(sort) && sort.length) {
      products.sort = sort;
    }

    const search = await postSearch({
      query: SEARCH_RESULTS_QUERY,
      variables: {
        accountId: resolvedAccountId,
        products,
        sessionParams,
      },
    });

    if (!search) {
      return empty;
    }

    if (track) {
      recordNostoSearch({
        type: track,
        request: { products },
        result: search,
      });
    }

    const result = search.products;
    const hits = result?.hits || [];

    return {
      productIds: hits.map((hit) => hit.productId).filter(Boolean),
      total: result?.total || 0,
      from: result?.from ?? from,
      size: result?.size ?? size,
      facets: result?.facets || [],
      ok: true,
    };
  } catch (error) {
    console.error("Nosto category request failed:", error);
    return empty;
  }
};

/**
 * Predictive/typeahead search for the header overlay.
 * Returns lightweight product hits and keyword suggestions ready for the UI.
 *
 * `track` ("autocomplete") sends the search to Nosto analytics.
 */
export const nostoPredictiveSearch = async ({
  accountId,
  currencyCode,
  query,
  size = 10,
  keywordSize = 5,
  track = null,
}) => {
  const resolvedAccountId = accountId || getNostoAccountId(currencyCode);

  if (!resolvedAccountId) {
    console.error("Nosto search: no account id available");
    return { products: [], keywords: [], redirect: null };
  }

  try {
    const sessionParams = await getSessionParams();

    const search = await postSearch({
      query: SEARCH_PREDICTIVE_QUERY,
      variables: {
        accountId: resolvedAccountId,
        query,
        products: { size },
        keywords: { size: keywordSize },
        sessionParams,
      },
    });

    if (track && search && !search.redirect) {
      recordNostoSearch({
        type: track,
        request: {
          query,
          products: { size },
          keywords: { size: keywordSize },
        },
        result: search,
      });
    }

    return {
      products: search?.products?.hits || [],
      keywords: search?.keywords?.hits || [],
      redirect: search?.redirect || null,
    };
  } catch (error) {
    console.error("Nosto predictive search request failed:", error);
    return { products: [], keywords: [], redirect: null };
  }
};
